import React, { useState, useEffect, useRef } from "react";
import FaIcon from "../fa-icon/fa-icon";
import IconEnum from "../../enums/icon.enum";
import IconLibraryEnum from "../../enums/icon-library.enum";
import ProductEnum from "../../enums/product-enum";
import EnvironmentEnum from "../../enums/environment.enum";
import SizeEnum from "../../enums/size.enum";
import classMap from "../../helpers/classMap";
import { ProductSelectorProps } from "./product-selector.props";
import { getProductTiles, ProductPortal, ProductTile } from "./product-tiles/product-tiles";
import "./product-selector.scss";

const ProductSelector = (props: ProductSelectorProps) => {
  const { currentProduct, licensedProducts, env, navigationCleanup, className } = props;
  const [showProducts, setShowProducts] = useState<boolean>(false);
  const productSelectorRef = useRef(null);
  const productTiles = getProductTiles(licensedProducts, currentProduct);

  const getProductIcon = (product: ProductEnum) => {
    return product === ProductEnum.IPA ? IconEnum.IL_PLUS_BRAND : IconEnum.MYPATH_BRAND;
  };

  const getEnvURL = (productTile: ProductTile) => {
    switch (env?.toLowerCase()) {
      case EnvironmentEnum.QA:
        return productTile.QaURL;
      case EnvironmentEnum.STAGING:
        return productTile.StagingURL;
      case EnvironmentEnum.PRODUCTION:
      default:
        return productTile.ProductionURL;
    }
  };

  const handleProductClick = async (
    event: React.MouseEvent<HTMLAnchorElement> | React.KeyboardEvent<HTMLAnchorElement>,
    productTile: ProductTile
  ) => {
    event.preventDefault();
    if (navigationCleanup) {
      await navigationCleanup();
    }
    window.location.href = getEnvURL(productTile);
  };

  const handleProductKeyDown = (event: React.KeyboardEvent<HTMLAnchorElement>, productTile: ProductTile) => {
    if (event.key === "Enter") {
      handleProductClick(event, productTile);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | FocusEvent) => {
      if (productSelectorRef.current && !productSelectorRef.current.contains(event.target as Node)) {
        setShowProducts(false);
      }
    };

    if (showProducts) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("focusin", handleClickOutside); // for keyboard navigation
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("focusin", handleClickOutside); // for keyboard navigation
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("focusin", handleClickOutside); // for keyboard navigation
    };
  }, [showProducts, productSelectorRef]);

  return (
    <div
      className={classMap(
        "product-selector",
        getProductIcon(currentProduct) === IconEnum.MYPATH_BRAND ? "mpng-style" : "",
        className
      )}
      ref={productSelectorRef}
      data-testid="product-selector"
    >
      <button
        onClick={() => setShowProducts((prev) => !prev)}
        className="product-selector-button"
        aria-label="Product Selector Dropdown"
      >
        <div className="brand-icon-selector"></div> {/* spacer to center the brand icon consistently */}
        <FaIcon icon={getProductIcon(currentProduct)} library={IconLibraryEnum.KIT} className="brand-icon" />
        <FaIcon icon={IconEnum.EXPAND_RIGHT} library={IconLibraryEnum.KIT} className="brand-icon-selector" />
      </button>
      <div
        className="product-tiles-container"
        data-testid="product-tiles-container"
        style={{
          gridTemplateColumns: productTiles.length > 0 ? "calc(-2px + 50%) calc(-2px + 50%)" : "none",
          width: productTiles.length > 0 ? "390px" : "195px",
          display: showProducts ? "grid" : "none"
        }}
      >
        {productTiles.map((productTile) => {
          return (
            <a
              href={getEnvURL(productTile)}
              onClick={(event) => handleProductClick(event, productTile)}
              onKeyDown={(event) => handleProductKeyDown(event, productTile)}
              className="product-tile"
              key={productTile.mappedProducts[0]}
            >
              <img src={productTile.image} alt={`${productTile.title} product-image`} />
            </a>
          );
        })}
        <a
          href={getEnvURL(ProductPortal)}
          onClick={(event) => handleProductClick(event, ProductPortal)}
          onKeyDown={(event) => handleProductKeyDown(event, ProductPortal)}
          className="product-portal-tile"
          aria-label={ProductPortal.title}
        >
          <FaIcon icon={IconEnum.GRID} size={SizeEnum.LARGE} className="product-portal-icon" />
          <span className="product-portal-text">{ProductPortal.title}</span>
        </a>
      </div>
    </div>
  );
};
export default ProductSelector;
